import moment from "moment-timezone";
import FormatDateHelper from "helper/FormatDateHelper";

export const NTT_ME_EXCLUSIVE_SEASON_IDS = new Set([
  "cmp4yz2lj00017rtcc4ok0jgv", // Regular Season 1
  "cmrz0wo8t0000bpzcana86bow", // Regular Season 2
]);

export const requiresNttMeOperatorFilter = (seasonId) => (
  !NTT_ME_EXCLUSIVE_SEASON_IDS.has(String(seasonId || ""))
);

const parseSeasonBoundary = (value) => {
  if (!value) return null;
  const text = String(value);
  return text.includes("T") || /Z$/.test(text)
    ? moment.utc(text)
    : moment.tz(text, "YYYY-MM-DD HH:mm", "Asia/Tokyo");
};

const formatUtc = (value) => value.utc().format("YYYY-MM-DD HH:mm:ss");

export const toNttPhotoShootUtcBounds = (dateRange, season) => {
  if (!Array.isArray(dateRange) || dateRange.length !== 2 || !season) {
    throw new Error("Invalid NTT-ME photo shoot range parameters");
  }

  const requestedStart = moment.tz(`${dateRange[0]} 00:00:00`, "Asia/Tokyo");
  const requestedEnd = moment
    .tz(`${dateRange[1]} 00:00:00`, "Asia/Tokyo")
    .add(1, "day");
  const seasonStart = parseSeasonBoundary(season.seasonStart);
  const seasonEnd = parseSeasonBoundary(season.seasonEnd);
  if (!seasonStart || !seasonEnd) {
    throw new Error("Missing NTT-ME season boundary");
  }
  const effectiveStart = moment.max(requestedStart, seasonStart);
  const effectiveEnd = moment.min(requestedEnd, seasonEnd);

  if (!effectiveStart.isBefore(effectiveEnd)) return null;

  return {
    startUtc: formatUtc(effectiveStart),
    endUtc: formatUtc(effectiveEnd),
  };
};

const buildOperatorPredicate = (seasonId) => (
  requiresNttMeOperatorFilter(seasonId)
    ? `
        AND EXISTS (
          SELECT 1
          FROM power_asset_detail pad
          WHERE pad.power_asset_id = pae.power_asset_id
            AND pad.type = 'NTT_ME'
        )`
    : ""
);

export const buildNttPhotoShootTrendQuery = ({ season, dateRange, grain = "day" }) => {
  if (!season || !season.seasonId) {
    throw new Error("Invalid NTT-ME season");
  }
  const bounds = toNttPhotoShootUtcBounds(dateRange, season);
  if (!bounds) return null;

  const bucket = grain === "hour"
    ? "DATE_FORMAT(DATE_ADD(pae.occurred_datetime, INTERVAL 9 HOUR), '%Y-%m-%d %H:00')"
    : "DATE(DATE_ADD(pae.occurred_datetime, INTERVAL 9 HOUR))";

  return {
    query: `
      SELECT
        ${bucket} AS bucket,
        COUNT(*) AS photograph_event_count,
        COUNT(DISTINCT pae.player_id) AS unique_player_count
      FROM power_asset_event pae
      WHERE pae.game_space_id = '${season.seasonId}'
        AND pae.type = 'PHOTOGRAPH'
        AND pae.occurred_datetime >= '${bounds.startUtc}'
        AND pae.occurred_datetime < '${bounds.endUtc}'
        ${buildOperatorPredicate(season.seasonId)}
      GROUP BY bucket
      ORDER BY bucket ASC;`,
    database: "KpiDashboard",
    type: "list",
  };
};

const normalizeRows = (response) => {
  if (Array.isArray(response)) return response;
  if (response && Array.isArray(response.body)) return response.body;
  if (response && response.body && typeof response.body === "string") {
    try { return JSON.parse(response.body); } catch (e) { return []; }
  }
  if (response && response.bucket !== undefined) return [response];
  if (response && response.body && response.body.bucket !== undefined) return [response.body];
  return [];
};

const normalizeBucket = (bucket, grain) => {
  if (bucket == null) return "";
  const value = String(bucket);
  if (value.includes("T")) {
    return grain === "hour"
      ? moment.utc(value).format("YYYY-MM-DD HH:mm")
      : moment.utc(value).format("YYYY-MM-DD");
  }
  return grain === "hour" ? value.slice(0, 16) : value.slice(0, 10);
};

const numericValue = (row, key) => {
  const value = row && row[key];
  return value == null ? 0 : Number(value);
};

export const normalizeNttPhotoShootTrend = ({ response, dateRange, grain = "day" }) => {
  const rows = normalizeRows(response);
  const dates = grain === "hour"
    ? FormatDateHelper.getHoursListBetweenDays(dateRange[0], dateRange[1])
    : FormatDateHelper.getDaysListBetweenDays(dateRange[0], dateRange[1]);
  const rowByBucket = new Map(rows.map((row) => [
    normalizeBucket(row.bucket || row.DAY || row.HOUR, grain),
    row,
  ]));

  return {
    date: dates,
    countDenchu: dates.map((date) => numericValue(rowByBucket.get(date), "photograph_event_count")),
    unique: dates.map((date) => numericValue(rowByBucket.get(date), "unique_player_count")),
    newUser: dates.map(() => null),
  };
};
